import React, { useEffect, useRef, useState } from 'react';
import Modal from '../../components/modal/Modal';
import CoachNotice from './CoachNotice';
import NewColorSquareButton from '../../components/Button/NewColorSquareButton';
import NewWhiteSquareButton from '../../components/Button/NewWhiteSquareButton';
import { useCoachCarryService, useCarryTargets } from '../../services/useCoachCarryService';
import {
    canDiscardCarryFailure, carryErrorMessage, carryResultView, carrySelectionFor, CarrySelection, carryStorageKey,
    CARRY_LIMIT_MESSAGE, createCarryRequest, readSavedCarry, SavedCarry, selectCarryMembers,
} from '../../lib/coachCarry';
import { CarryMember, CarryRequest, CarryResult } from '../../types/coachCarry';
import { useAccessMode } from '../../context/AccessModeContext';
import { CARRY_FIXED_FEE_NOTICE, carryMemberDetails, CarryMemberDetails, carryNoticeMessage, carryUnpaidTotal, sharedCarryWarnings } from '../../lib/coachCarryDisplay';

type Props = {
    centerId: string;
    fromCenterName: string;
    coachId: string;
    coachName: string;
    actorId: string;
    accessToken: string;
    onClose: () => void;
};

const number = (value: number) => value.toLocaleString('ko-KR');
const pendingResults = (body: CarryRequest): CarryResult[] => body.memberIds.map((memberId) => ({
    memberId, outcome: 'PENDING_CONFIRMATION', status: 202,
}));

const MemberDetails = ({ details }: { details?: CarryMemberDetails }) => {
    const [expanded, setExpanded] = useState(false);
    const collapsible = (details?.memberships.length || 0) >= 4;
    const memberships = collapsible && !expanded ? details?.memberships.slice(0, 2) : details?.memberships;
    return <>
        {memberships?.map((membership, index) => (
            <p key={index} title={membership.title} className="mt-[6px] text-[13px] leading-[20px]">
                {membership.text}
                {membership.unpaid && <strong className="text-ERROR"> · {membership.unpaid}</strong>}
            </p>
        ))}
        {collapsible && <button type="button" aria-expanded={expanded} onClick={() => setExpanded(!expanded)}
            className="mt-[6px] text-[13px] text-primary underline">
            {expanded ? '접기' : `외 ${number((details?.memberships.length || 0) - 2)}개 보기`}
        </button>}
        {details?.warnings.map((warning, index) => <p key={index} className="mt-[6px] text-[13px] leading-[20px] text-LAVEL_2"><CoachNotice message={warning} /></p>)}
    </>;
};

const ModalCoachCarry = ({ centerId, fromCenterName, coachId, coachName, actorId, accessToken, onClose }: Props) => {
    const { isReadOnly } = useAccessMode();
    const storageKey = carryStorageKey(actorId, centerId, coachId);
    const [saved, setSaved] = useState<SavedCarry | null>(() => readSavedCarry(storageKey));
    const [step, setStep] = useState<'target' | 'members' | 'consent' | 'result'>(saved ? 'result' : 'target');
    const [selection, setSelection] = useState<CarrySelection | null>(() => saved ? {
        coachId, toCenterId: saved.body.toCenterId, idempotencyKey: saved.body.idempotencyKey,
    } : null);
    const [members, setMembers] = useState<CarryMember[]>([]);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [consent, setConsent] = useState(false);
    const [results, setResults] = useState<CarryResult[]>(() => saved ? pendingResults(saved.body) : []);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [searchName, setSearchName] = useState('');
    const [requestFailed, setRequestFailed] = useState(!!saved);
    const [requestRejected, setRequestRejected] = useState(false);
    const sending = useRef(false);
    const title = useRef<HTMLHeadingElement>(null);
    useEffect(() => { title.current?.focus(); }, [step]);
    const targets = useCarryTargets(centerId, coachId, accessToken, actorId);
    const { preview, execute } = useCoachCarryService(centerId, coachId, accessToken);
    const busy = preview.isLoading || execute.isLoading;
    const hasPending = results.some((result) => carryResultView(result).pending);
    const keyReused = results.some((result) => carryResultView(result).restart);
    const targetName = saved?.targetName
        || targets.data?.data.targets?.find((target) => target.centerId === selection?.toCenterId)?.centerName || '';
    const centerNames = { fromCenterName, targetName };
    const selectedMembers = members.filter((member) => selectedIds.includes(member.memberId));
    const visibleMembers = members.filter((member) => (member.memberName || '이름 없는 회원').includes(searchName.trim()));
    const availableMembers = visibleMembers.filter((member) => member.canCarry);
    const blockedMembers = visibleMembers.filter((member) => !member.canCarry);
    const sharedWarnings = step === 'result' ? saved?.sharedWarnings || []
        : sharedCarryWarnings(step === 'consent' ? selectedMembers : members).map((warning) => carryNoticeMessage(warning, centerNames));
    const completedCount = results.filter((result) => ['COMMITTED', 'REPLAYED'].includes(result.outcome)).length;
    const resultCount = saved?.body.memberIds.length || results.length;
    const rateTableMissing = saved?.rateTableMissing ?? sharedWarnings.some((warning) => warning.includes('요율표가 없어요'));
    const unpaidTotal = carryUnpaidTotal(results);
    const nextSteps = [
        ...(completedCount > 0 ? [CARRY_FIXED_FEE_NOTICE] : []),
        ...(rateTableMissing ? [`${targetName} 관리자에게 이 강사의 요율표 설정을 요청해 주세요`] : []),
        ...(unpaidTotal === null ? [`미수가 있는지 ${fromCenterName} 미수 목록에서 확인해 주세요`]
            : unpaidTotal > 0 ? [`미수 ${number(unpaidTotal)}원은 ${fromCenterName} 미수 목록에서 받아 주세요`] : []),
    ];

    const loadPreview = async () => {
        if (!selection || busy) return;
        setError('');
        const response = await preview.mutateAsync(selection.toCenterId);
        if (response.status === 200 && Array.isArray(response.data.members)) {
            setMembers(response.data.members);
            setSearchName('');
            if (!saved) {
                setSelectedIds([]);
                setConsent(false);
            }
            setStep('members');
        } else {
            setError(carryErrorMessage(response.status, response.data));
        }
    };

    const chooseMembers = (ids: string[]) => {
        const next = selectCarryMembers(members, ids);
        setSelectedIds(next.memberIds);
        setConsent(false);
        setNotice(next.exceeded ? CARRY_LIMIT_MESSAGE : '');
    };

    const run = async () => {
        if (sending.current || isReadOnly || !selection || (!saved && (!consent || selectedIds.length === 0))) return;
        sending.current = true;
        // 재시도 가능한 회원별 실패 뒤 전체 요청이 거절돼도 원래 키·본문을 유지한다.
        const hasPreviousAttempt = !!saved;
        setError('');
        const snapshot: SavedCarry = saved || {
            body: createCarryRequest(selection, selectedIds, consent),
            memberNames: Object.fromEntries(members.map((member) => [member.memberId, member.memberName || '이름 없는 회원'])),
            targetName,
            memberDetails: Object.fromEntries(selectedMembers.map((member) => [member.memberId, carryMemberDetails(member, centerNames)])),
            sharedWarnings,
            rateTableMissing: selectedMembers.some((member) => member.warnings.some((warning) => warning.code === 'TARGET_RATE_TABLE_MISSING')),
        };
        try {
            // 첫 전송 전에 저장한다. 모달 재진입·새로고침 후에도 같은 본문으로 확인한다.
            sessionStorage.setItem(storageKey, JSON.stringify(snapshot));
        } catch {
            setError('실행 정보를 보관하지 못했어요. 브라우저 설정을 확인하고 다시 시도해 주세요.');
            sending.current = false;
            return;
        }
        setSaved(snapshot);
        setStep('result');
        setResults((current) => current.length ? current : pendingResults(snapshot.body));
        try {
            const response = await execute.mutateAsync(snapshot.body);
            if ((response.status === 200 || response.status === 202) && Array.isArray(response.data.results)) {
                const byId = new Map(response.data.results.filter((result) => result
                    && typeof result.memberId === 'string' && Number.isInteger(result.status)
                    && result.status >= 100 && result.status <= 599
                    && ['COMMITTED', 'REPLAYED', 'PENDING_CONFIRMATION', 'FAILED'].includes(result.outcome))
                    .map((result) => [result.memberId, result]));
                // 일부 결과가 빠진 응답도 완료로 간주하지 않는다.
                const next = pendingResults(snapshot.body).map((pending) => byId.get(pending.memberId) || pending);
                setResults(next);
                setRequestFailed(false);
                if (next.every((result) => !carryResultView(result).pending && !carryResultView(result).retry)) {
                    sessionStorage.removeItem(storageKey);
                }
            } else {
                setError(carryErrorMessage(response.status, response.data));
                setRequestFailed(true);
                if (response.data.errorCode === 'CARRY_IDEMPOTENCY_KEY_REUSED') {
                    setResults(snapshot.body.memberIds.map((memberId) => ({
                        memberId, outcome: 'FAILED', status: response.status, ...response.data,
                    })));
                } else if (response.data.results === undefined
                    && canDiscardCarryFailure(response.status, hasPreviousAttempt, response.data.errorCode)) {
                    sessionStorage.removeItem(storageKey);
                    setSaved(null);
                    setResults([]);
                    setRequestFailed(false);
                    setRequestRejected(true);
                    setConsent(false);
                    setStep('members');
                }
            }
        } finally {
            sending.current = false;
        }
    };

    const startAgain = () => {
        if (busy || hasPending || (requestFailed && !keyReused)) return;
        sessionStorage.removeItem(storageKey);
        setSaved(null);
        setResults([]);
        setMembers([]);
        setSelectedIds([]);
        setConsent(false);
        setError('');
        setNotice('');
        setSearchName('');
        setRequestFailed(false);
        if (keyReused || requestRejected) setSelection(null);
        setRequestRejected(false);
        setStep('target');
    };

    const renderMember = (member: CarryMember) => (
        <article key={member.memberId} aria-label={member.memberName || '이름 없는 회원'} className="border-b border-solid border-GREY_200 py-[12px] last:border-b-0">
            <label className="flex items-center gap-[8px] font-bold">
                {!saved && <input type="checkbox" disabled={!member.canCarry}
                    checked={selectedIds.includes(member.memberId)}
                    onChange={(event) => chooseMembers(event.target.checked
                        ? [...selectedIds, member.memberId] : selectedIds.filter((id) => id !== member.memberId))} />}
                {member.memberName || '이름 없는 회원'}
                {saved?.body.memberIds.includes(member.memberId) && <span className="text-[13px] font-normal text-primary">이번 요청에 포함된 회원</span>}
            </label>
            {member.blockers.length > 0 && <p className="mt-[4px] text-[13px] leading-[20px] text-ERROR">
                {Array.from(new Set(member.blockers.map((blocker) => carryNoticeMessage(blocker, centerNames)))).map((message) => <span key={message} className="mt-[8px] block"><CoachNotice message={message} /></span>)}
            </p>}
            {!member.canCarry && member.blockers.length === 0 && <p className="mt-[4px] text-[13px] text-ERROR">지금은 이동할 수 없어요.<span className="mt-[4px] block">회원의 수강권 상태를 확인하고, 해결되지 않으면 라포에 알려 주세요.</span></p>}
            <MemberDetails details={carryMemberDetails(member, centerNames)} />
        </article>
    );

    return (
        <Modal whiteViewWidth={800} isCloseOutside={false} onClose={() => { if (!sending.current) onClose(); }}>
            <section role="dialog" aria-modal="true" aria-labelledby="carry-title" className="flex max-h-[calc(100vh-48px)] flex-col p-[32px] text-[14px] text-LAVEL_4">
                <p className="mb-[8px] text-[13px] text-LAVEL_2">{coachName} · 회원 이어받기</p>
                <h2 id="carry-title" ref={title} tabIndex={-1} className="mb-[24px] text-[22px] font-bold focus:outline-none">
                    {{ target: '받는 센터를 선택해 주세요', members: '이어받을 회원을 선택해 주세요',
                        consent: '회원 동의를 확인해 주세요', result: completedCount === resultCount && resultCount > 0
                            ? `${number(resultCount)}명 모두 이어받았어요`
                            : `${number(resultCount)}명 중 ${number(completedCount)}명 이어받았어요 · ${number(resultCount - completedCount)}명 확인 필요` }[step]}
                </h2>
                {error && <p role="alert" className="mb-[16px] rounded-[8px] bg-ERROR_50 p-[16px] text-ERROR"><CoachNotice message={error} /></p>}
                {step !== 'target' && <p className="mb-[12px]">보내는 센터: <strong>{fromCenterName}</strong> · 받는 센터: <strong>{targetName}</strong></p>}
                {step !== 'target' && step !== 'result' && (
                    <aside aria-label="이어받기 공통 안내" className="mb-[16px] shrink-0 space-y-[6px] rounded-[8px] bg-WHITE_500 p-[12px] text-[13px] leading-[20px] text-LAVEL_2">
                        <p>{CARRY_FIXED_FEE_NOTICE}</p>
                        {sharedWarnings.map((warning, index) => <p key={index}><CoachNotice message={warning} /></p>)}
                    </aside>
                )}
                {step === 'result' && nextSteps.length > 0 && (
                    <aside aria-label="다음 할 일" className="mb-[16px] shrink-0 rounded-[8px] border border-solid border-GREY_200 p-[12px] text-[13px] leading-[20px]">
                        <h3 className="mb-[6px] font-bold">다음 할 일</h3>
                        <ul className="list-disc space-y-[6px] pl-[18px]">
                            {nextSteps.map((item) => <li key={item}>{item}</li>)}
                        </ul>
                    </aside>
                )}
                {step === 'target' && (
                    <>
                        {targets.isFetching ? <p>받는 센터를 불러오고 있어요.</p> : targets.data?.status !== 200 ? (
                            <div role="alert">
                                <CoachNotice message={carryErrorMessage(targets.data?.status ?? 0, targets.data?.data, true)} />
                                <button className="mt-[12px] text-primary underline" onClick={() => targets.refetch()}>다시 불러오기</button>
                            </div>
                        ) : targets.data.data.targets?.length === 0 ? (
                            <CoachNotice message="선택할 수 있는 받는 센터가 없어요. 받는 센터에 강사가 등록되어 있는지 확인해 주세요." />
                        ) : (
                            <label className="flex flex-col gap-[8px]">
                                받는 센터
                                <select aria-label="받는 센터" value={selection?.toCenterId || ''} disabled={busy}
                                    className="h-[48px] rounded-[4px] border border-solid border-GREY_200 px-[12px]"
                                    onChange={(event) => {
                                        setSelection(event.target.value ? carrySelectionFor(selection, coachId, event.target.value) : null);
                                        setError('');
                                    }}>
                                    <option value="">센터를 선택해 주세요</option>
                                    {targets.data.data.targets?.map((target) => (
                                        <option key={target.centerId} value={target.centerId}>{target.centerName}</option>
                                    ))}
                                </select>
                            </label>
                        )}
                    </>
                )}
                {step === 'members' && (
                    <>
                        {members.length > 0 && (
                            <input type="search" aria-label="회원 이름 검색" placeholder="회원 이름으로 검색해 주세요"
                                value={searchName} onChange={(event) => setSearchName(event.target.value)}
                                className="mb-[12px] h-[40px] shrink-0 rounded-[4px] border border-solid border-GREY_200 px-[12px]" />
                        )}
                        {!saved && members.length > 0 && (
                            <div className="mb-[8px] flex shrink-0 items-center justify-between gap-[12px] border-b border-solid border-GREY_200 pb-[12px]">
                                <label className="flex items-center gap-[8px]">
                                    <input type="checkbox" aria-label="가능한 회원 전체 선택" disabled={availableMembers.length === 0}
                                        checked={availableMembers.length > 0 && availableMembers.filter((member) => selectedIds.includes(member.memberId)).length === Math.min(30, availableMembers.length)}
                                        onChange={(event) => chooseMembers(event.target.checked
                                            ? [...selectedIds, ...availableMembers.map((member) => member.memberId)]
                                            : selectedIds.filter((id) => !availableMembers.some((member) => member.memberId === id)))} />
                                    {searchName.trim() ? '검색한 회원 전체 선택' : '가능한 회원 전체 선택'}
                                </label>
                                <span className="whitespace-nowrap text-primary">{number(selectedIds.length)}명 선택 · 최대 30명</span>
                            </div>
                        )}
                        {notice && <p role="status" className="mb-[12px] text-ERROR">{notice}<span className="mt-[4px] block text-[13px] leading-[21px]">선택한 회원 수를 줄여 주세요.</span></p>}
                        {members.length === 0 && <p>이어받을 회원이 없어요.</p>}
                        {members.length > 0 && visibleMembers.length === 0 && <p>검색한 이름의 회원이 없어요.</p>}
                        <div aria-label="이어받기 회원 목록" className="min-h-0 max-h-[440px] overflow-y-auto pr-[8px]">
                            {availableMembers.map(renderMember)}
                            {blockedMembers.length > 0 && (
                                <details open={searchName.trim() ? true : undefined} className="mt-[12px] rounded-[8px] border border-solid border-GREY_200 px-[16px]">
                                    <summary className="cursor-pointer py-[12px] font-medium text-LAVEL_2">지금 옮길 수 없는 회원 {number(blockedMembers.length)}명</summary>
                                    {blockedMembers.map(renderMember)}
                                </details>
                            )}
                        </div>
                    </>
                )}
                {step === 'consent' && (
                    <>
                        <p className="mb-[12px]"><strong>{targetName}</strong>에서 선택한 <strong>{number(selectedIds.length)}명</strong>의 수강권을 이어받아요.</p>
                        <p className="mb-[12px] shrink-0 text-[13px] leading-[20px] text-LAVEL_2">회원 앱에서는 원래 수강권이 만료로, 받는 센터 수강권이 새로 보여요.</p>
                        <div className="mb-[20px] min-h-0 max-h-[320px] overflow-y-auto pr-[8px]">
                            {selectedMembers.map((member) => (
                                <article key={member.memberId} aria-label={member.memberName || '이름 없는 회원'} className="border-b border-solid border-GREY_200 py-[12px] last:border-b-0">
                                    <p className="font-bold">{member.memberName || '이름 없는 회원'}</p>
                                    <MemberDetails details={carryMemberDetails(member, centerNames)} />
                                </article>
                            ))}
                        </div>
                        <label className="flex shrink-0 items-center gap-[8px]">
                            <input type="checkbox" checked={consent} onChange={(event) => setConsent(event.target.checked)} />
                            회원에게 이동 사실을 알리고 동의를 받았어요
                        </label>
                    </>
                )}
                {step === 'result' && (
                    <>
                        {hasPending && <p className="mb-[16px]">이동 결과를 확인하고 있어요. 다시 확인해도 같은 요청으로 안전하게 이어서 처리해요.</p>}
                        <div aria-live="polite" className="min-h-0 max-h-[400px] overflow-y-auto pr-[8px]">
                            {results.map((result) => {
                                const view = carryResultView(result);
                                return (
                                    <article key={result.memberId} className="border-b border-solid border-GREY_200 py-[12px] last:border-b-0">
                                        <div className="flex items-center justify-between gap-[12px]">
                                            <strong>{saved?.memberNames[result.memberId] || '확인이 필요한 회원'}</strong>
                                            <span className={`shrink-0 rounded-full px-[10px] py-[4px] text-[13px] font-bold ${view.label === '완료'
                                                ? 'bg-SUCCESS_50 text-SUCCESS' : view.pending ? 'bg-WARNING_50 text-WARNING' : 'bg-ERROR_50 text-ERROR'}`}>{view.label}</span>
                                        </div>
                                        {result.outcome === 'FAILED' && <div className="mt-[8px] flex items-start justify-between gap-[12px]">
                                            <p className="text-[13px] leading-[20px] text-ERROR">{Array.from(new Set([
                                                carryErrorMessage(result.status, result),
                                                ...(result.details?.blockers || []).map((blocker) => carryNoticeMessage(blocker, centerNames)),
                                            ])).map((message) => <span key={message} className="mb-[8px] block last:mb-0"><CoachNotice message={message} /></span>)}</p>
                                            {view.retry && <button type="button" disabled={busy || isReadOnly} onClick={run}
                                                aria-label={`${saved?.memberNames[result.memberId] || '회원'} 결과 · 전체 요청 재시도`}
                                                className="shrink-0 text-[13px] text-primary underline disabled:opacity-50">전체 요청 재시도</button>}
                                        </div>}
                                        {result.outcome !== 'FAILED' && !!result.details?.blockers?.length && (
                                            <p className="mt-[8px] text-[13px] leading-[20px] text-ERROR">{Array.from(new Set(
                                                result.details.blockers.map((blocker) => carryNoticeMessage(blocker, centerNames))
                                            )).map((message) => <span key={message} className="mb-[8px] block last:mb-0"><CoachNotice message={message} /></span>)}</p>
                                        )}
                                        {saved?.memberDetails?.[result.memberId] && !['COMMITTED', 'REPLAYED'].includes(result.outcome) && (
                                            <p className="mt-[8px] text-[13px] text-LAVEL_2">요청 당시 수강권 정보예요. 이동 완료 여부는 위 상태를 확인해 주세요.</p>
                                        )}
                                        <MemberDetails details={saved?.memberDetails?.[result.memberId]} />
                                    </article>
                                );
                            })}
                        </div>
                    </>
                )}
                <div className="mt-[24px] flex shrink-0 flex-wrap justify-end gap-[12px]">
                    <NewWhiteSquareButton text="닫기" onClick={() => { if (!busy) onClose(); }} width={90} height={44} />
                    {step === 'target' && <NewColorSquareButton text={busy ? '불러오는 중' : '미리보기'} disabled={busy || !selection || targets.data?.status !== 200} onClick={loadPreview} width={120} height={44} />}
                    {step === 'members' && (saved ? (
                        <NewColorSquareButton text="결과로 돌아가기" onClick={() => setStep('result')} width={150} height={44} />
                    ) : (
                        <>
                            <NewWhiteSquareButton text={requestRejected ? '처음부터 다시' : '센터 다시 선택'} onClick={requestRejected ? startAgain : () => { setStep('target'); setNotice(''); }} width={130} height={44} />
                            {!requestRejected && <NewColorSquareButton text="다음" disabled={selectedIds.length === 0} onClick={() => setStep('consent')} width={100} height={44} />}
                        </>
                    ))}
                    {step === 'consent' && (
                        <>
                            <NewWhiteSquareButton text="회원 다시 선택" onClick={() => setStep('members')} width={130} height={44} />
                            <NewColorSquareButton text="동의하고 이어받기" disabled={!consent || busy || isReadOnly} onClick={run} width={160} height={44} />
                        </>
                    )}
                    {step === 'result' && (
                        <>
                            {results.some((result) => ['CARRY_TARGET_SET_CHANGED', 'CARRY_SOURCE_CHANGED'].includes(result.errorCode || '')) && (
                                <NewWhiteSquareButton text="미리보기 다시 확인" onClick={loadPreview} width={160} height={44} />
                            )}
                            {!hasPending && (!requestFailed || keyReused) && (
                                <NewWhiteSquareButton text={keyReused ? '처음부터 다시 시작' : '다른 회원 이어받기'} onClick={startAgain} width={160} height={44} />
                            )}
                            {((requestFailed && !keyReused) || results.some((result) => carryResultView(result).retry)) && (
                                <NewColorSquareButton text={busy ? '확인하고 있어요' : hasPending || requestFailed ? '다시 확인' : '같은 요청으로 재시도'}
                                    disabled={busy || isReadOnly} onClick={run} width={170} height={44} />
                            )}
                        </>
                    )}
                </div>
            </section>
        </Modal>
    );
};

export default ModalCoachCarry;
